import React from 'react';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { PlayerAvatar } from '@/components/match/PlayerRatings';
import { AppText } from '@/components/ui/AppText';
import { Card } from '@/components/ui/Card';
import { formatNumber, latinNumerals } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import type { PredictionBoardEntry } from '@/lib/supabase/types';

const PODIUM = ['#F4C84B', '#C7CDDA', '#CD8B53']; // gold · silver · bronze

/** Ranked prediction-leaderboard rows; the signed-in user's row is highlighted.
 *  Always renders Latin digits so columns stay aligned in either locale. */
export function BoardRows({
  entries,
  myUserId,
}: {
  entries: PredictionBoardEntry[];
  myUserId: string | null;
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const nums = latinNumerals(locale);
  return (
    <Card className="mx-4 overflow-hidden">
      {entries.map((entry, index) => {
        const isMine = entry.user_id === myUserId;
        const medal = entry.rank <= 3 ? PODIUM[entry.rank - 1] : null;
        return (
          <View
            key={entry.user_id}
            className={`flex-row items-center gap-3 px-4 py-3 ${
              index > 0 ? 'border-t border-line' : ''
            } ${isMine ? 'bg-volt/10' : ''}`}
          >
            {medal ? (
              <View
                className="h-7 w-7 items-center justify-center rounded-full"
                style={{ backgroundColor: medal }}
              >
                <AppText weight="bold" tabular style={{ color: '#1A1408', fontSize: 12 }}>
                  {formatNumber(entry.rank, nums)}
                </AppText>
              </View>
            ) : (
              <AppText variant="caption" weight="bold" tabular className="w-7 text-center text-muted">
                {formatNumber(entry.rank, nums)}
              </AppText>
            )}
            <PlayerAvatar uri={entry.avatar_url} name={entry.display_name} size={34} />
            <View className="flex-1">
              <AppText variant="caption" weight="bold" className="text-ink" numberOfLines={1}>
                {entry.display_name}
                {isMine ? `  ·  ${t('fantasy.leaderboards.you')}` : ''}
              </AppText>
              <AppText variant="micro" className="text-muted" numberOfLines={1}>
                {t('predictions.record', {
                  correct: formatNumber(entry.correct, nums),
                  played: formatNumber(entry.played, nums),
                })}
              </AppText>
            </View>
            <AppText variant="body" weight="bold" tabular className="text-volt-bright">
              {formatNumber(entry.total_points, nums)}
            </AppText>
          </View>
        );
      })}
    </Card>
  );
}
